// DashboardModals.js - Updated with proper centering and user edit functionality

import React, { useState, useEffect } from 'react';
import QuoteDashboardModal from './QuoteDashboardModal';
import LoadingWidget from '../../../components/LoadingWidget';

const DashboardModals = ({
  showViewModal,
  showAddModal,
  showEditModal,
  showDeleteModal,
  showQuoteModal,
  selectedItem,
  modalType,
  newItem,
  setShowViewModal,
  setShowAddModal,
  setShowEditModal,
  setShowDeleteModal,
  setShowQuoteModal,
  setNewItem,
  setModalType,
  handleAddItem,
  handleEditItem,
  handleDeleteItem,
  getDisplayName,
  users,
  API_BASE_URL,
  refetchAll,
  showAlert,
  isClientView = false,
  isProcessing = false
}) => {
  // State for status update (now part of edit)
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [editedItem, setEditedItem] = useState({});  // For general edit fields
  const [isUpdating, setIsUpdating] = useState(false); // For user updates

  // Available status options for quotations
  const statusOptions = [
    { value: 'submitted', label: 'Submitted', color: 'blue' },
    { value: 'contacted', label: 'Contacted', color: 'yellow' },
    { value: 'converted', label: 'Converted to Sale', color: 'green' },
    { value: 'cancelled', label: 'Cancelled', color: 'red' },
    { value: 'expired', label: 'Expired', color: 'gray' }
  ];

  // Get status badge color
  const getStatusColor = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'gray';
  };

  // Initialize editedItem when selectedItem changes in edit mode
  // Initialize editedItem when selectedItem changes in edit mode
  useEffect(() => {
    if (showEditModal && selectedItem) {
      // Make sure we have all the fields properly mapped
      setEditedItem({
        ...selectedItem,
        // Ensure profile fields are at the top level for the form
        phone: selectedItem.phone || selectedItem.profile?.phone || '',
        address: selectedItem.address || selectedItem.profile?.address || '',
      });

      // For clients, always start with empty status (they need to select "cancelled")
      // For admins, start with current status
      if (isClientView) {
        setNewStatus('');  // Start with no selection for clients
      } else {
        setNewStatus(selectedItem.status || '');
      }
    }
  }, [showEditModal, selectedItem, isClientView]);

  // Handle status update (now integrated with edit)
  const handleStatusUpdate = async () => {
    if (!newStatus) {
      showAlert('error', 'Validation Error', 'Please select a status');
      return;
    }

    // Check if trying to update an expired quotation
    if (selectedItem.status === 'expired') {
      showAlert('error', 'Update Blocked', 'Cannot update status of an expired quotation. User must request a new quotation.');
      return;
    }

    setStatusUpdateLoading(true);

    try {
      const token = localStorage.getItem('authToken');

      // Use the convert_to_sale endpoint if converting, otherwise use regular update
      if (newStatus === 'converted') {
        const response = await fetch(
          `${API_BASE_URL}/api/quotations/${selectedItem.id}/convert_to_sale/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          showAlert('success', 'Quotation Converted',
            `${data.message} — Converted by: ${data.quotation.converted_by}. ${data.followups_updated.message}`
          );

          // Refresh data
          if (refetchAll) refetchAll();

          // Close modals
          setShowEditModal(false);
          setShowViewModal(false);
        } else {
          showAlert('error', 'Conversion Failed', data.message || 'Failed to convert quotation');
        }
      } else {
        // Regular status update
        const response = await fetch(
          `${API_BASE_URL}/api/quotations/${selectedItem.id}/update_status/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: newStatus })
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          showAlert('success', 'Status Updated', data.message);

          // Refresh data
          if (refetchAll) refetchAll();

          // Close modals
          setShowEditModal(false);
          setShowViewModal(false);
        } else {
          showAlert('error', 'Update Failed', data.error || 'Failed to update status');
        }
      }
    } catch (error) {
      showAlert('error', 'Update Failed', 'Failed to update status. Please try again.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Handle user update - FIXED VERSION with proper profile field handling
  const handleUserUpdate = async (e) => {
    e.preventDefault();

    if (!selectedItem || !selectedItem.id) {
      showAlert('error', 'Error', 'No user selected');
      return;
    }

    // Only validate if username was actually changed
    if (editedItem.username !== selectedItem.username) {
      if (editedItem.username && editedItem.username.trim() !== '') {
        if (editedItem.username.length < 3) {
          showAlert('error', 'Validation Error', 'Username must be at least 3 characters');
          return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(editedItem.username)) {
          showAlert('error', 'Validation Error', 'Username can only contain letters, numbers, and underscores');
          return;
        }
      }
    }

    // Only validate if email was actually changed
    if (editedItem.email !== selectedItem.email) {
      if (editedItem.email && editedItem.email.trim() !== '') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedItem.email)) {
          showAlert('error', 'Validation Error', 'Please enter a valid email address');
          return;
        }
      }
    }

    // Phone validation (optional) - only if changed
    if (editedItem.phone !== selectedItem.phone) {
      if (editedItem.phone && editedItem.phone.trim() !== '') {
        const cleanedPhone = editedItem.phone.replace(/[^\d+]/g, '');
        const phoneRegex = /^\+?[1-9]\d{0,14}$/;
        if (!phoneRegex.test(cleanedPhone)) {
          showAlert('error', 'Validation Error', 'Please enter a valid phone number');
          return;
        }
      }
    }

    setIsUpdating(true);

    try {
      const token = localStorage.getItem('authToken');

      // Prepare data for update - following the serializer's expected format
      const updateData = {};

      // User fields - only include if changed
      if (editedItem.username !== selectedItem.username) {
        updateData.username = editedItem.username;
      }

      if (editedItem.first_name !== selectedItem.first_name) {
        updateData.first_name = editedItem.first_name || '';
      }

      if (editedItem.last_name !== selectedItem.last_name) {
        updateData.last_name = editedItem.last_name || '';
      }

      if (editedItem.email !== selectedItem.email) {
        updateData.email = editedItem.email;
      }

      // Profile fields - need to be nested under 'profile' as the serializer expects
      const profileUpdates = {};

      if (editedItem.phone !== selectedItem.phone) {
        profileUpdates.phone = editedItem.phone || '';
      }

      if (editedItem.address !== selectedItem.address) {
        profileUpdates.address = editedItem.address || '';
      }

      // Only add profile object if there are profile updates
      if (Object.keys(profileUpdates).length > 0) {
        updateData.profile = profileUpdates;
      }

      // If no fields were changed, just close the modal
      if (Object.keys(updateData).length === 0) {
        showAlert('info', 'No Changes', 'No changes were made to the user.');
        setShowEditModal(false);
        setEditedItem({});
        setIsUpdating(false);
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/rest-auth/admin/update-user/${selectedItem.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );

      const data = await response.json();

      if (response.ok) {
        showAlert('success', 'Success', 'User details updated successfully!');

        // Refresh data
        if (refetchAll) refetchAll();

        // Close modal
        setShowEditModal(false);
        setEditedItem({});
      } else {
        // Handle validation errors
        let errorMessage = 'Failed to update user:\n';

        if (data.user) {
          // Handle nested user errors
          Object.keys(data.user).forEach(key => {
            if (Array.isArray(data.user[key])) {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${data.user[key][0]}`;
            }
          });
        } else if (data.profile) {
          // Handle nested profile errors
          Object.keys(data.profile).forEach(key => {
            if (Array.isArray(data.profile[key])) {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${data.profile[key][0]}`;
            }
          });
        } else {
          // Handle direct errors
          Object.keys(data).forEach(key => {
            if (Array.isArray(data[key])) {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${data[key][0]}`;
            } else if (typeof data[key] === 'string') {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${data[key]}`;
            }
          });
        }

        showAlert('error', 'Update Failed', errorMessage, 3000);
      }
    } catch (error) {
      showAlert('error', 'Error', 'An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // View Modal - Organized with service-type-specific sections
  const renderViewModal = () => {
    if (!showViewModal || !selectedItem) return null;

    const title = `${getDisplayName(selectedItem, modalType)} Details`;
    const isQuotation = modalType === 'quotation';
    const serviceType = selectedItem.service_type; // 'solar' | 'electrical' | 'generator'

    // Helper to render a labeled field
    const Field = ({ label, value, fullWidth = false }) => {
      const display = value === null || value === undefined || value === '' ? 'N/A' : String(value);
      return (
        <div className={fullWidth ? 'col-span-2' : ''}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
            {label}
          </dt>
          <dd className="text-sm text-gray-900 break-words">{display}</dd>
        </div>
      );
    };

    // Section wrapper
    const Section = ({ icon, title: sectionTitle, colorClass = 'blue', children }) => (
      <div className={`mb-6 rounded-xl border border-${colorClass}-200${colorClass}-800 overflow-hidden`}>
        <div className={`px-4 py-2.5 bg-${colorClass}-50${colorClass}-900/20 flex items-center gap-2`}>
          <span className="text-lg">{icon}</span>
          <h4 className={`text-sm font-semibold text-${colorClass}-800${colorClass}-200`}>{sectionTitle}</h4>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {children}
        </div>
      </div>
    );

    // Render structured quotation view
    const renderQuotationDetails = () => {
      const q = selectedItem;
      return (
        <>
          {/* ── Status Banner ── */}
          <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Quotation #{q.id} &nbsp;·&nbsp; {q.service_type_display || q.service_type}
                </p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${getStatusColor(q.status)}-100 text-${getStatusColor(q.status)}-800${getStatusColor(q.status)}-900${getStatusColor(q.status)}-200`}>
                  {q.status_display || q.status}
                </span>
              </div>
              <div className="text-right text-xs text-gray-500">
                <p>Created: {q.created_at ? new Date(q.created_at).toLocaleDateString() : 'N/A'}</p>
                <p>Updated: {q.updated_at ? new Date(q.updated_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>

            {/* Expired warning */}
            {q.status === 'expired' && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg className="h-5 w-5 text-red-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-red-800">
                  <p className="font-medium">Expired Quotation</p>
                  <p className="mt-0.5">This quotation has expired and cannot be updated. User must request a new quotation.</p>
                </div>
              </div>
            )}

            {/* Conversion details */}
            {q.status === 'converted' && q.converted_by_username && (
              <div className="mt-3 pt-3 border-t border-blue-200 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500 text-xs font-semibold uppercase">Converted by</span>
                  <p className="font-medium text-gray-900">{q.converted_by_username}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs font-semibold uppercase">Converted at</span>
                  <p className="font-medium text-gray-900">
                    {q.converted_at ? new Date(q.converted_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Customer Information ── */}
          <Section icon="👤" title="Customer Information" colorClass="gray">
            <Field label="Customer Name" value={q.user_info?.name || q.customer_name} />
            <Field label="Email" value={q.user_info?.email || q.customer_email} />
            <Field label="Phone" value={q.phone} />
            <Field label="Address" value={q.address} fullWidth />
            <Field label="Preferred Contact" value={q.preferred_contact_display || q.preferred_contact} />
            <Field label="Best Time to Contact" value={q.best_time_to_contact_display || q.best_time_to_contact} />
            <Field label="Preferred Date" value={q.preferred_date} />
          </Section>

          {/* ── SOLAR-SPECIFIC SECTION ── */}
          {serviceType === 'solar' && (
            <>
              <Section icon="☀️" title="Energy Consumption" colorClass="yellow">
                <Field label="Monthly Bill (SZL)" value={q.monthly_bill} />
                <Field label="Monthly Usage (kWh)" value={q.monthly_units_kwh} />
                <Field label="Daily Usage (kWh)" value={q.daily_usage_kwh} />
                <Field label="Sunlight Hours / Day" value={q.sunlight_hours} />
              </Section>

              <Section icon="🏠" title="Site & Roof Details" colorClass="yellow">
                <Field label="Roof Type" value={q.roof_type_display || q.roof_type} />
                <Field label="Roof Size (m²)" value={q.roof_size_m2} />
                <Field label="Used Roof Space (m²)" value={q.used_roof_space_m2} />
                <Field label="Roof Usage %" value={q.roof_usage_percentage != null ? `${q.roof_usage_percentage}%` : null} />
                <Field label="Roof Limited?" value={q.is_roof_limited != null ? (q.is_roof_limited ? 'Yes' : 'No') : null} />
                <Field label="Extra Panels Possible" value={q.extra_panels_possible} />
                <Field label="Extra Roof Space Available (m²)" value={q.extra_roof_space_available} />
              </Section>

              <Section icon="⚡" title="System Configuration" colorClass="yellow">
                <Field label="System Type" value={q.system_type_display || q.system_type} />
                <Field label="Battery Backup" value={q.battery_backup != null ? (q.battery_backup ? 'Yes' : 'No') : null} />
                <Field label="Backup Hours Needed" value={q.backup_hours_needed} />
                <Field label="Maintenance Plan" value={q.maintenance_plan_display || q.maintenance_plan} />
              </Section>

              <Section icon="🔧" title="Calculated System Specs" colorClass="yellow">
                <Field label="Required System Size (kW)" value={q.required_system_size_kw} />
                <Field label="Max Possible System (kW)" value={q.max_possible_system_kw} />
                <Field label="Actual System Size (kW)" value={q.actual_system_size_kw} />
                <Field label="Panel Count" value={q.panel_count} />
                <Field label="Inverter Size (kW)" value={q.inverter_size_kw} />
                <Field label="Inverter Type" value={q.inverter_type} />
                <Field label="Battery Size (kWh)" value={q.battery_size_kwh} />
                <Field label="Daily Production (kWh)" value={q.daily_production_kwh} />
                <Field label="Monthly Production (kWh)" value={q.monthly_production_kwh} />
                <Field label="Coverage %" value={q.coverage_percentage != null ? `${q.coverage_percentage}%` : null} />
              </Section>

              <Section icon="💰" title="Cost Breakdown" colorClass="green">
                <Field label="Panel Cost" value={q.panel_cost != null ? `SZL ${Number(q.panel_cost).toLocaleString()}` : null} />
                <Field label="Inverter Cost" value={q.inverter_cost != null ? `SZL ${Number(q.inverter_cost).toLocaleString()}` : null} />
                <Field label="Battery Cost" value={q.battery_cost != null ? `SZL ${Number(q.battery_cost).toLocaleString()}` : null} />
                <Field label="Mounting Cost" value={q.mounting_cost != null ? `SZL ${Number(q.mounting_cost).toLocaleString()}` : null} />
                <Field label="Electrical Cost" value={q.electrical_cost != null ? `SZL ${Number(q.electrical_cost).toLocaleString()}` : null} />
                <Field label="Labour Cost" value={q.labor_cost != null ? `SZL ${Number(q.labor_cost).toLocaleString()}` : null} />
                <Field label="Misc Costs" value={q.misc_costs != null ? `SZL ${Number(q.misc_costs).toLocaleString()}` : null} />
                <Field label="Maintenance Cost" value={q.maintenance_cost != null ? `SZL ${Number(q.maintenance_cost).toLocaleString()}` : null} />
                <Field label="Company Margin" value={q.company_margin != null ? `SZL ${Number(q.company_margin).toLocaleString()}` : null} />
                <Field label="Equipment Total" value={q.equipment_total != null ? `SZL ${Number(q.equipment_total).toLocaleString()}` : null} />
                <Field label="Installation Total" value={q.installation_total != null ? `SZL ${Number(q.installation_total).toLocaleString()}` : null} />
                <Field label="Subtotal" value={q.subtotal != null ? `SZL ${Number(q.subtotal).toLocaleString()}` : null} />
                <Field label="Total Cost" value={q.total_cost != null ? `SZL ${Number(q.total_cost).toLocaleString()}` : null} />
                <Field label="Monthly Savings" value={q.monthly_savings != null ? `SZL ${Number(q.monthly_savings).toLocaleString()}` : null} />
                <Field label="Annual Savings" value={q.annual_savings != null ? `SZL ${Number(q.annual_savings).toLocaleString()}` : null} />
                <Field label="Payback Period (yrs)" value={q.payback_years} />
                <Field label="ROI %" value={q.roi_percentage != null ? `${q.roi_percentage}%` : null} />
              </Section>
            </>
          )}

          {/* ── ELECTRICAL-SPECIFIC SECTION ── */}
          {serviceType === 'electrical' && (
            <>
              <Section icon="🔌" title="Electrical Service Details" colorClass="blue">
                <Field label="Property Type" value={q.property_type_display || q.property_type} />
                <Field label="Property Size" value={q.property_size_display || q.property_size} />
                <Field label="Urgency" value={q.urgency_display || q.urgency} />
                <Field label="Additional Details" value={q.additional_details} fullWidth />
              </Section>

            </>
          )}

          {/* ── GENERATOR-SPECIFIC SECTION ── */}
          {serviceType === 'generator' && (
            <>
              <Section icon="🔋" title="Generator Service Details" colorClass="orange">
                <Field label="Service Type" value={q.generator_service_type_display || q.generator_service_type} />
                <Field label="Generator Size (kVA)" value={q.generator_kva_range_display || q.generator_kva_range} />
                <Field label="Purpose" value={q.generator_purpose_display || q.generator_purpose} />
                <Field label="Fuel Type" value={q.generator_fuel_type_display || q.generator_fuel_type} />
                <Field label="Hire Duration" value={q.generator_hire_duration_display || q.generator_hire_duration} />
                <Field label="Additional Details" value={q.generator_additional_details} fullWidth />
              </Section>
            </>
          )}

          {/* ── Notes ── */}
          {(q.additional_notes || q.internal_notes) && (
            <Section icon="📝" title="Notes" colorClass="gray">
              {q.additional_notes && <Field label="Additional Notes" value={q.additional_notes} fullWidth />}
              {q.internal_notes && <Field label="Internal Notes (Staff Only)" value={q.internal_notes} fullWidth />}
            </Section>
          )}
        </>
      );
    };

    // Render generic (non-quotation) view - kept as fallback
    const renderGenericDetails = () => (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(selectedItem).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="col-span-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                  {key.replace(/_/g, ' ')}
                </h4>
                <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
                  {JSON.stringify(value, null, 2)}
                </pre>
              </div>
            );
          }
          return (
            <div key={key}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5 capitalize">
                {key.replace(/_/g, ' ')}
              </dt>
              <dd className="text-sm text-gray-900 break-words">
                {value === null ? 'N/A' : value.toString()}
              </dd>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[88vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center z-10">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {isQuotation ? renderQuotationDetails() : renderGenericDetails()}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add Modal - UPDATED with proper centering
  const renderAddModal = () => {
    if (!showAddModal) return null;

    const title = modalType === 'user' ? 'Add New User' : `Add New ${modalType.charAt(0).toUpperCase() + modalType.slice(1).replace('-', ' ')}`;

    const getFormFields = () => {
      switch (modalType) {
        case 'user':
          return (
            <>
              {/* Two-column layout for Name fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={newItem.first_name || ''}
                    onChange={(e) => setNewItem({ ...newItem, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="First name (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={newItem.last_name || ''}
                    onChange={(e) => setNewItem({ ...newItem, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Last name (optional)"
                  />
                </div>
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  required
                  value={newItem.username || ''}
                  onChange={(e) => setNewItem({ ...newItem, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Choose a username"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Username can only contain letters, numbers, and underscores
                </p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={newItem.email || ''}
                  onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="user@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={newItem.phone || ''}
                  onChange={(e) => setNewItem({ ...newItem, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+266 1234 5678"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Phone number will be used for quotation follow-ups
                </p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  value={newItem.address || ''}
                  onChange={(e) => setNewItem({ ...newItem, address: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="User's address (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Address will be used for service location and quotations
                </p>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={newItem.password || ''}
                    onChange={(e) => setNewItem({ ...newItem, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Create a strong password"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must be at least 8 characters with uppercase, lowercase, numbers, and special characters
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={newItem.password2 || ''}
                    onChange={(e) => setNewItem({ ...newItem, password2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              {/* Info message about verification */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Account Creation Process:</p>
                    <p className="mt-1">The user will receive a verification email at the address provided. They must verify their email before they can log in.</p>
                  </div>
                </div>
              </div>

              {/* Staff checkbox */}
              <div className="mt-4 flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center h-5 mt-0.5">
                  <input
                    id="is_staff"
                    type="checkbox"
                    checked={newItem.is_staff || false}
                    onChange={(e) => setNewItem({ ...newItem, is_staff: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="is_staff" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Staff Member
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {newItem.is_staff
                      ? 'This user will have staff privileges and access to the admin dashboard.'
                      : 'This user will be created as a regular user with no admin privileges.'}
                  </p>
                </div>
              </div>
            </>
          );

        case 'quotation':
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User *
                </label>
                <select
                  required
                  value={newItem.customer || ''}
                  onChange={(e) => setNewItem({ ...newItem, customer: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type *
                </label>
                <select
                  required
                  value={newItem.service_type || ''}
                  onChange={(e) => setNewItem({ ...newItem, service_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select service type</option>
                  <option value="solar">Solar Energy Only</option>
                  <option value="electrical">Electrical Services Only</option>
                  <option value="both">Both Services</option>
                </select>
              </div>
            </>
          );

        case 'document':
          return (
            <>
              {/* Quotation selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attach to Quotation *
                </label>
                <select
                  required
                  value={newItem.quotation || ''}
                  onChange={(e) => setNewItem({ ...newItem, quotation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a quotation</option>
                  {(newItem._quotationsList || []).map(q => (
                    <option key={q.id} value={q.id}>
                      #{q.id} — {q.user_info?.name || q.customer_name || 'Unknown'} ({q.service_type_display || q.service_type})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The document will be linked to the selected quotation.
                </p>
              </div>

              {/* Document type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type *
                </label>
                <select
                  required
                  value={newItem.document_type || ''}
                  onChange={(e) => setNewItem({ ...newItem, document_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select document type</option>
                  {/* Clients can only submit site photos and other supporting files.
                      Staff-generated docs (proposal, contract, invoice, permit) are
                      admin-only and will be uploaded by staff on behalf of the client. */}
                  {isClientView ? (
                    <>
                      <option value="site_photo">Site Photo</option>
                      <option value="other">Other Supporting Document</option>
                    </>
                  ) : (
                    <>
                      <option value="proposal">Proposal</option>
                      <option value="contract">Contract</option>
                      <option value="invoice">Invoice</option>
                      <option value="site_photo">Site Photo</option>
                      <option value="permit">Permit</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors">
                  <div className="space-y-1 text-center">
                    {newItem._file ? (
                      <>
                        <svg className="mx-auto h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium text-gray-900">{newItem._file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(newItem._file.size / 1024).toFixed(1)} KB
                        </p>
                        <button
                          type="button"
                          onClick={() => setNewItem({ ...newItem, _file: null })}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                        >
                          Remove file
                        </button>
                      </>
                    ) : (
                      <>
                        <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                            <span>Upload a file</span>
                            <input
                              type="file"
                              required
                              className="sr-only"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) setNewItem({ ...newItem, _file: file });
                              }}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, Word, Excel, images up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newItem.description || ''}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Brief description of this document (optional)"
                />
              </div>

              {/* Info banner */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800">
                    {isClientView
                      ? 'You can attach site photos or supporting documents to help us process your quotation. Official documents like proposals and contracts are prepared by our team.'
                      : 'Documents are attached to an existing quotation after it has been submitted. You cannot attach files during the initial quotation request.'}
                  </p>
                </div>
              </div>
            </>
          );

        case 'subscriber':
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                required
                value={newItem.email || ''}
                onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          );

        default:
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name/Title *
              </label>
              <input
                type="text"
                required
                value={newItem.name || newItem.title || ''}
                onChange={(e) => setNewItem({
                  ...newItem,
                  [modalType === 'followup' ? 'notes' : 'name']: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          );
      }
    };

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
        <div className="flex min-h-full items-center justify-center p-4 relative">

          {/* Processing overlay — sits over the entire modal including scrollable area */}
          {isProcessing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-gray-900/40 backdrop-blur-sm">
              <LoadingWidget />
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg max-w-6xl w-full max-h-[80vh] overflow-y-auto">

            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            </div>
            <form onSubmit={handleAddItem} className="p-6 space-y-4">
              {getFormFields()}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewItem({});
                    setModalType('');
                  }}
                  disabled={isProcessing}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Adding...' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Render status section for quotations in edit modal
  const renderQuotationStatusSection = () => {
    if (modalType !== 'quotation') return null;

    const isExpired = selectedItem.status === 'expired';
    const isConverted = selectedItem.status === 'converted';

    // Filter status options based on user role
    const getFilteredStatusOptions = () => {
      if (isClientView) {
        // Clients can only cancel their quotations
        return statusOptions.filter(opt => opt.value === 'cancelled');
      } else {
        // Admin can see all status options
        return statusOptions;
      }
    };

    const filteredStatusOptions = getFilteredStatusOptions();

    return (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Status
          </label>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${getStatusColor(selectedItem.status)}-100 text-${getStatusColor(selectedItem.status)}-800${getStatusColor(selectedItem.status)}-900${getStatusColor(selectedItem.status)}-200`}>
            {selectedItem.status_display || selectedItem.status}
          </span>
        </div>

        {/* NEW: Show client-specific message */}
        {isClientView && selectedItem.status !== 'cancelled' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Client Status Update</p>
                <p className="mt-1">As a client, you can only cancel this quotation. For other status updates, please contact our team.</p>
              </div>
            </div>
          </div>
        )}

        {/* Expired quotation warning - disable all status changes */}
        {isExpired && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-red-800">
                <p className="font-medium">Expired Quotation</p>
                <p className="mt-1">This quotation has expired. Status cannot be changed. The user must request a new quotation.</p>
              </div>
            </div>
          </div>
        )}

        {/* NEW: Show message if already cancelled */}
        {isClientView && selectedItem.status === 'cancelled' && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-gray-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <div className="text-sm text-gray-800">
                <p className="font-medium">Quotation Cancelled</p>
                <p className="mt-1">This quotation has already been cancelled. No further status updates are needed.</p>
              </div>
            </div>
          </div>
        )}

        {/* Only show status dropdown if NOT expired AND (admin OR (client AND not cancelled)) */}
        {!isExpired && (
          <>
            {/* Show different messages based on role and status */}
            {isClientView && selectedItem.status === 'cancelled' ? (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm text-blue-800">
                  <p>This quotation has been cancelled. If you need to request a new quotation, please use the "Request Quote" button.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isClientView ? 'Cancel Quotation' : 'Update Status *'}
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={isExpired || (isClientView && selectedItem.status === 'cancelled')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isClientView ? (
                      // Clients only see Cancel option - using filteredStatusOptions
                      <>
                        <option value="">Select action</option>
                        {filteredStatusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </>
                    ) : (
                      // Admin sees all status options
                      <>
                        <option value="">Select new status</option>
                        {statusOptions.map(option => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={option.value === selectedItem.status ||
                              (isConverted && option.value !== 'converted')}
                          >
                            {option.label}
                            {option.value === selectedItem.status ? ' (Current)' : ''}
                            {isConverted && option.value !== 'converted' ? ' (Cannot change from converted)' : ''}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Warning for cancellation - for both admin and client */}
                {newStatus === 'cancelled' && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="text-sm text-red-800">
                        <p className="font-medium">Warning: Cancelling Quotation</p>
                        <p className="mt-1">This action will mark the quotation as cancelled. This cannot be undone. All follow-ups will be marked as cancelled.</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Admin-only warnings */}
        {!isClientView && !isExpired && newStatus === 'converted' && selectedItem.status !== 'converted' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Converting to Sale</p>
                <p className="mt-1">This will automatically:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Mark all incomplete follow-ups as completed</li>
                  <li>Record you as the converter</li>
                  <li>Set the conversion timestamp</li>
                  <li>Send notifications to the team</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Admin-only: Already converted warning */}
        {!isClientView && !isExpired && selectedItem.status === 'converted' && newStatus !== 'converted' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-red-800">
                <p className="font-medium">Warning</p>
                <p className="mt-1">This quotation has already been converted to a sale. Changing the status may affect your records.</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin-only: Status change effects info */}
        {!isClientView && !isExpired && newStatus && newStatus !== selectedItem.status && newStatus !== 'converted' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Status Change Effects</p>
                <p className="mt-1">
                  {newStatus === 'submitted' ? 'All follow-ups will be updated to "Scheduled".' :
                    newStatus === 'contacted' ? 'All follow-ups will be updated to "In Progress".' :
                      newStatus === 'cancelled' ? 'All follow-ups will be updated to "Cancelled".' :
                        newStatus === 'expired' ? 'All follow-ups will be updated to "No Response".' :
                          ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Unsubscribe Confirmation Modal (used instead of generic edit modal for subscribers)
  const renderUnsubscribeModal = () => {
    if (!showEditModal || !selectedItem || modalType !== 'subscriber') return null;
    const isCurrentlyActive = selectedItem.is_active;
    const action = isCurrentlyActive ? 'unsubscribe' : 'resubscribe';
    const actionLabel = isCurrentlyActive ? 'Unsubscribe' : 'Resubscribe';
    const actionColor = isCurrentlyActive
      ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 focus:ring-yellow-500'
      : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 focus:ring-green-500';

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-xl w-full relative">

            {isProcessing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-gray-900/40 backdrop-blur-sm">
                <LoadingWidget />
              </div>
            )}

            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Confirm {actionLabel}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Are you sure you want to <strong>{action}</strong> <span className="font-medium text-gray-900">{selectedItem.email}</span>?
                {isCurrentlyActive
                  ? ' They will no longer receive newsletter emails.'
                  : ' They will start receiving newsletter emails again.'}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={isProcessing}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => handleEditItem(e)}
                  disabled={isProcessing}
                  className={`px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${actionColor}`}
                >
                  {isProcessing ? `${actionLabel}ing...` : actionLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Edit Modal - UPDATED with proper centering and user edit fields
  const renderEditModal = () => {
    if (!showEditModal || !selectedItem) return null;

    // Subscribers use their own confirmation modal instead
    if (modalType === 'subscriber') return null;

    const isQuotation = modalType === 'quotation';
    const isExpired = isQuotation && selectedItem.status === 'expired';

    const handleEditSubmit = (e) => {
      e.preventDefault();
      if (isQuotation) {
        // For quotations, handle status update
        handleStatusUpdate();
      } else if (modalType === 'user') {
        // For users, handle profile update
        handleUserUpdate(e);
      } else {
        // For other item types, call the original handleEditItem
        handleEditItem(e);
      }
    };

    // Determine if submit button should be disabled
    const isSubmitDisabled = () => {
      if (statusUpdateLoading || isUpdating) return true;

      if (!isQuotation) {
        if (modalType === 'user') {
          // For users, check if any field has actually changed
          const hasChanges = (
            editedItem.username !== selectedItem?.username ||
            editedItem.first_name !== selectedItem?.first_name ||
            editedItem.last_name !== selectedItem?.last_name ||
            editedItem.email !== selectedItem?.email ||
            editedItem.phone !== selectedItem?.phone ||
            editedItem.address !== selectedItem?.address
          );

          // If no changes, disable the button
          if (!hasChanges) return true;

          // Also require username and email to be present
          return !editedItem.username || !editedItem.email;
        }
        return false;
      }

      if (isExpired) return true;

      // For clients: disabled if no status selected OR if status is cancelled but already cancelled
      if (isClientView) {
        // If no status selected (empty string), button should be disabled
        if (!newStatus || newStatus === '') return true;
        // If already cancelled, button should be disabled
        if (selectedItem.status === 'cancelled') return true;
        // Otherwise enabled (client can only select 'cancelled' from dropdown)
        return false;
      }

      // For admin: disabled if no status selected
      return !newStatus || newStatus === '';
    };

    // Get submit button text
    const getSubmitButtonText = () => {
      if (statusUpdateLoading || isUpdating) {
        if (isQuotation) return 'Updating Status...';
        if (modalType === 'user') return 'Updating User...';
        return 'Saving...';
      }

      if (!isQuotation) {
        if (modalType === 'user') return 'Save Changes';
        return 'Save Changes';
      }

      if (isExpired) {
        return 'Expired (Cannot Update)';
      }

      if (isClientView) {
        return selectedItem.status === 'cancelled' ? 'Already Cancelled' : 'Cancel Quotation';
      }

      return 'Update Status';
    };

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-xl w-full max-h-[80vh] overflow-y-auto relative">

            {/* Processing overlay inside the modal */}
            {(statusUpdateLoading || isUpdating) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-gray-900/40 backdrop-blur-sm">
                <LoadingWidget />
              </div>
            )}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Edit {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
              </h3>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6">

              {/* Status update section for quotations */}
              {isQuotation ? (
                renderQuotationStatusSection()
              ) : modalType === 'user' ? (
                // User edit fields
                <div className="space-y-4">
                  {/* Two-column layout for Name fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editedItem.first_name || ''}
                        onChange={(e) => setEditedItem({ ...editedItem, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editedItem.last_name || ''}
                        onChange={(e) => setEditedItem({ ...editedItem, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      required
                      value={editedItem.username || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Username"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={editedItem.email || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="user@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={editedItem.phone || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+266 1234 5678"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      value={editedItem.address || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, address: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="User's address"
                    />
                  </div>

                  {/* Note about password */}
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium">Password Change:</p>
                        <p className="mt-1">To change the user's password, please use the password reset functionality. Password cannot be changed through this form.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Regular edit fields for other non-quotation items
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name/Title
                  </label>
                  <input
                    type="text"
                    value={editedItem.name || editedItem.title || ''}
                    onChange={(e) => setEditedItem({ ...editedItem, [modalType === 'followup' ? 'notes' : 'name']: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditedItem({});
                    setNewStatus('');
                  }}
                  disabled={statusUpdateLoading || isUpdating}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitDisabled()}
                  className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                    // If it's client view, always show red button (since they can only cancel)
                    isClientView
                      ? 'bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white'
                      // For admin user edit, show blue button
                      : modalType === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white'
                        // For admin, show red only when cancelling quotation, otherwise blue
                        : newStatus === 'cancelled'
                          ? 'bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white'
                          : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white'
                    }`}
                >
                  {(statusUpdateLoading || isUpdating) ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {getSubmitButtonText()}
                    </>
                  ) : (
                    getSubmitButtonText()
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal - UPDATED with proper centering
  const renderDeleteModal = () => {
    if (!showDeleteModal || !selectedItem) return null;

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-xl w-full max-w-screen overflow-y-auto relative">

            {/* Processing overlay inside the modal */}
            {isProcessing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-gray-900/40 backdrop-blur-sm">
                <LoadingWidget />
              </div>
            )}

            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Confirm Delete</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this {modalType}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isProcessing}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteItem}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-800 text-white rounded-lg hover:from-red-700 hover:to-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderViewModal()}
      {renderAddModal()}
      {renderUnsubscribeModal()}
      {renderEditModal()}
      {renderDeleteModal()}

      {/* Quote Modal for creating new quotations */}
      <QuoteDashboardModal
        show={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        users={users}
        API_BASE_URL={API_BASE_URL}
        refetchAll={refetchAll}
        showAlert={showAlert}
      />
    </>
  );
};

export default DashboardModals;